<script>
import { buildBars }          from '../js/midiChartGeometry.js';
import { formatStreamLabel }  from '../js/midiLabels.js';
import { EFFECT_ACCENT }      from '../js/effects.js';

const SVG_W = 300;
const SVG_H = 80;

export default {
  name: 'MidiMonitorChart',

  inject: ['midiMonitor'],

  props: {
    chartKey: {
      type: String,
      required: true,
    },
    // Raw stream object from MidiStreamStore: { channel, cc, latestValue, ... }
    stream: {
      type: Object,
      required: true,
    },
    // Bucket array (Array(BUCKET_COUNT) of number|null) from the store
    buckets: {
      type: Array,
      required: true,
    },
    // App settings (for device/CC name lookup)
    settings: {
      type: Object,
      default: null,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
    dimmed: {
      type: Boolean,
      default: false,
    },
    // Effect type string (e.g. 'swell', 'bounce') or null if no match
    effectType: {
      type: String,
      default: null,
    },
  },

  computed: {
    label() {
      return formatStreamLabel(
        this.stream.channel,
        this.stream.cc,
        this.settings?.devices ?? null,
      );
    },

    latestValue() {
      return this.stream.latestValue ?? 0;
    },

    bars() {
      return buildBars(this.buckets, SVG_W, SVG_H);
    },

    accentColor() {
      return (this.effectType && EFFECT_ACCENT[this.effectType]) ?? null;
    },

    cardStyle() {
      if (!this.accentColor) return {};
      return {
        '--chart-accent':  this.accentColor,
        '--chart-latest':  this.accentColor,
        '--chart-highlight': this.accentColor,
      };
    },
  },
};
</script>

<template>
  <div
    class="midi-monitor-panel__chart-card"
    :class="{ 'midi-monitor-panel__chart-card--highlight': highlighted, 'midi-monitor-panel__chart-card--dimmed': dimmed }"
    :style="cardStyle"
    @mouseenter="midiMonitor.key = chartKey"
    @mouseleave="midiMonitor.key = null"
  >
    <div class="midi-monitor-panel__chart-header">
      <span class="midi-monitor-panel__channel-label">{{ label }}</span>
      <span class="midi-monitor-panel__value-badge">{{ latestValue }}</span>
    </div>

    <svg
      class="midi-monitor-panel__svg"
      viewBox="0 0 300 80"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <!-- Grid lines at 0%, 25%, 50%, 75%, 100% of y-range -->
      <line x1="0" y1="0"  x2="300" y2="0"  class="midi-monitor-panel__grid-line" />
      <line x1="0" y1="20" x2="300" y2="20" class="midi-monitor-panel__grid-line" />
      <line x1="0" y1="40" x2="300" y2="40" class="midi-monitor-panel__grid-line" />
      <line x1="0" y1="60" x2="300" y2="60" class="midi-monitor-panel__grid-line" />
      <line x1="0" y1="80" x2="300" y2="80" class="midi-monitor-panel__grid-line midi-monitor-panel__grid-line--base" />

      <rect
        v-for="(bar, i) in bars"
        :key="i"
        :x="bar.x"
        :y="bar.y"
        :width="bar.width"
        :height="bar.height"
        :class="[
          'midi-monitor-panel__bar',
          bar.empty  ? 'midi-monitor-panel__bar--empty'  : '',
          bar.latest ? 'midi-monitor-panel__bar--latest' : '',
        ]"
      />
    </svg>

    <div class="midi-monitor-panel__x-axis">
      <span>−10s</span>
      <span>−5s</span>
      <span>now</span>
    </div>
  </div>
</template>
